import { useEffect, useState, useMemo, useRef } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    getTaskDetails, deleteTask, cloneTask, transitionTask,
    assignTask, selfAssignTask, reassignTask, delegateTask, removeAssignee,
    addWatcher, removeWatcher,
    getComments, addComment, uploadCommentAttachments,
    uploadAttachment, deleteAttachment,
    getTaskHistory,
} from '../../../redux/actions/SocietyPortal/TaskAction/TaskAction';
import { getEmployeesDropdown } from '../../../redux/actions/SocietyPortal/EmployeeAction/EmployeeAction';
import { downloadAttachmentReq } from '../../../axios/request/SocietyPortal/TaskReqs/TaskReqs';
import SearchableDropdown from '../../../components/SearchableDropdown';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import usePermissions from '../../../hook/usePermissions';
import { PERMISSIONS } from '../../../consts/permissions';
import { notify } from '../../../utils/notification';
import { usePageRefresh } from '../../../context/PageRefreshContext';
import {
    getAllowedTransitions, getTransitionPermission,
    statusBadgeClass, priorityBadgeClass, humanize,
} from '../../../consts/taskConstants';
import { formatIstDate, formatIstDateTime, formatIstTime } from '../../../utils/helpers/istDate';

const fmt = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return formatIstDateTime(d);
};
const fmtDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return formatIstDate(d);
};
const userName = (u) => (u ? `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || `User #${u.user_id}` : '—');
const initials = (u) => {
    if (!u) return '?';
    const s = `${(u.first_name || '').charAt(0)}${(u.last_name || '').charAt(0)}`.toUpperCase();
    return s || (u.email || 'U').charAt(0).toUpperCase();
};

// Chat helpers — clock time on the bubble, day separators between groups.
const chatTime = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return formatIstTime(d);
};
const dayKey = (value) => {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toDateString();
};
const dayLabel = (value) => {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return formatIstDate(d);
};
// Deterministic avatar colour so each participant is recognisable at a glance.
const AVATAR_COLORS = [
    'tw-bg-teal-100 tw-text-teal-700', 'tw-bg-indigo-100 tw-text-indigo-700',
    'tw-bg-rose-100 tw-text-rose-700', 'tw-bg-amber-100 tw-text-amber-700',
    'tw-bg-violet-100 tw-text-violet-700', 'tw-bg-sky-100 tw-text-sky-700',
    'tw-bg-emerald-100 tw-text-emerald-700', 'tw-bg-orange-100 tw-text-orange-700',
];
const avatarColor = (u) => {
    const seed = String(u?.user_id ?? u?.email ?? '?');
    let hash = 0;
    for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) % 997;
    return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

// Comment attachments: the API accepts at most 10 files of 500 KB each per request.
const MAX_COMMENT_FILES = 10;
const MAX_COMMENT_FILE_SIZE = 500 * 1024;
const API_ORIGIN = (process.env.REACT_APP_NODE_MOBILE_BASE_URL || '').replace(/\/$/, '');
// Uploads are served statically, so images can be previewed inline from file_path.
const fileUrl = (att) => (att?.file_path ? `${API_ORIGIN}/${String(att.file_path).replace(/^\//, '')}` : '');
const isImage = (att) => /^image\//.test(att?.file_type || '');
const fileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    return bytes < 1024 ? `${bytes} B` : `${Math.round(bytes / 1024)} KB`;
};
const fileIcon = (att) => {
    const t = att?.file_type || '';
    if (t.startsWith('image/')) return 'mdi:file-image-outline';
    if (t === 'application/pdf') return 'mdi:file-pdf-box';
    if (t.includes('sheet') || t.includes('excel') || t.includes('csv')) return 'mdi:file-excel-outline';
    if (t.includes('word') || t.includes('document')) return 'mdi:file-word-outline';
    if (t.startsWith('video/')) return 'mdi:file-video-outline';
    if (t.includes('zip') || t.includes('compressed')) return 'mdi:folder-zip-outline';
    return 'mdi:file-outline';
};

// The workflow "state group" shown as the big label above the status pill.
const STATE_GROUP = {
    PENDING: 'OPEN', ASSIGNED: 'OPEN', ACCEPTED: 'OPEN', IN_PROGRESS: 'OPEN',
    WAITING_APPROVAL: 'OPEN', ON_HOLD: 'OPEN', REOPENED: 'OPEN',
    COMPLETED: 'CLOSED', CLOSED: 'CLOSED',
    CANCELLED: 'CANCELLED', REJECTED: 'CANCELLED',
};
const stateGroup = (s) => STATE_GROUP[s] || 'OPEN';

const inputCls = 'tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 tw-bg-white';
const cardCls = 'tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100';
const sideLabel = 'tw-inline-flex tw-items-center tw-text-[11px] tw-font-bold tw-text-teal-700 tw-tracking-wider tw-uppercase tw-bg-teal-50 tw-px-2 tw-py-1 tw-rounded-md';

const ViewTask = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const dispatch = useDispatch();
    const { can } = usePermissions();
    const societyId = selectedSocietyStorer.getSocietyId();

    const {
        taskDetailsData: task, taskDetailsLoading,
        taskCommentsData, taskCommentsLoading,
        taskHistoryData, taskHistoryLoading,
        transitionTaskLoading, deleteTaskLoading, cloneTaskLoading,
        addCommentLoading, uploadCommentAttachmentsLoading,
    } = useSelector((s) => s.TaskReducer);
    const { employeesDropdownData, employeesDropdownLoading } = useSelector((s) => s.EmployeeReducer);
    const { loginData } = useSelector((s) => s.loginReducer);
    const currentUserId = loginData?.user?.user_id ?? loginData?.user?.id;
    const isMe = (u) => currentUserId != null && u?.user_id != null && String(u.user_id) === String(currentUserId);
    // Only the uploader may remove an attachment — everyone else just downloads it.
    const isMyUpload = (att) => currentUserId != null && att?.uploaded_by != null && String(att.uploaded_by) === String(currentUserId);

    // Assign / reassign / delegate options — employees with a linked user account.
    const employeeOptions = useMemo(() => (Array.isArray(employeesDropdownData) ? employeesDropdownData : [])
        .filter((e) => e.user_id != null)
        .map((e) => {
            const name = `${e.first_name || ''} ${e.last_name || ''}`.trim();
            return { value: String(e.user_id), label: e.employee_type ? `${name} — ${e.employee_type}` : name };
        }), [employeesDropdownData]);

    // Map user_id -> display name so assignee history entries (which store raw
    // user IDs) can be shown as names instead of numbers.
    const userNameById = useMemo(() => {
        const map = {};
        (Array.isArray(employeesDropdownData) ? employeesDropdownData : []).forEach((e) => {
            if (e.user_id == null) return;
            const name = `${e.first_name || ''} ${e.last_name || ''}`.trim();
            const label = name || e.email || `User #${e.user_id}`;
            map[String(e.user_id)] = name && e.email ? `${name} (${e.email})` : label;
        });
        return map;
    }, [employeesDropdownData]);

    // Resolve a raw history value to something human-readable. Assignee/watcher
    // events store user IDs; everything else is shown verbatim.
    const historyValue = (h, value) => {
        if (!value) return '—';
        const idFields = ['assignee', 'watcher'];
        if (idFields.includes(h.field_name) || ['ASSIGNED', 'REASSIGNED', 'UNASSIGNED', 'DELEGATED', 'WATCHER_ADDED', 'WATCHER_REMOVED'].includes(h.event_type)) {
            return userNameById[String(value)] || value;
        }
        return value;
    };

    const [tab, setTab] = useState('comments');
    const [commentBody, setCommentBody] = useState('');
    const [commentInternal, setCommentInternal] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [cloneOpen, setCloneOpen] = useState(false);
    const [cloneTitle, setCloneTitle] = useState('');
    const [assignModal, setAssignModal] = useState(null); // 'assign' | 'reassign' | 'delegate'
    const [assignUserId, setAssignUserId] = useState('');
    const [assignNote, setAssignNote] = useState('');
    const [watcherId, setWatcherId] = useState('');
    const [downloading, setDownloading] = useState({}); // attachment_id -> percent (0-100)
    const [dragOver, setDragOver] = useState(false);
    const [commentFiles, setCommentFiles] = useState([]); // staged files for the next comment
    const [attachToCommentId, setAttachToCommentId] = useState(null); // comment receiving extra files
    const chatBoxRef = useRef(null);
    const composerRef = useRef(null);
    const commentFileInputRef = useRef(null);
    const commentAttachInputRef = useRef(null);

    const refetch = () => dispatch(getTaskDetails({ taskId: id }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { refetch(); }, [dispatch, id]);
    useEffect(() => { dispatch(getEmployeesDropdown({ societyId })); }, [dispatch, societyId]);
    useEffect(() => {
        if (tab === 'comments') dispatch(getComments({ taskId: id }));
        if (tab === 'history') dispatch(getTaskHistory({ taskId: id }));
    }, [tab, dispatch, id]);

    // Header refresh: the task plus whichever of comments / history is the open
    // tab. The employee dropdown is not reloaded.
    usePageRefresh(() => {
        refetch();
        if (tab === 'comments') dispatch(getComments({ taskId: id }));
        if (tab === 'history') dispatch(getTaskHistory({ taskId: id }));
    });

    const allowedTransitions = useMemo(() => (task ? getAllowedTransitions(task.status) : []), [task]);
    const comments = Array.isArray(taskCommentsData) ? taskCommentsData : [];
    const history = Array.isArray(taskHistoryData) ? taskHistoryData : [];

    // Keep the chat pinned to the newest message (scrolls the thread, not the page).
    useEffect(() => {
        const box = chatBoxRef.current;
        if (tab === 'comments' && box) box.scrollTop = box.scrollHeight;
    }, [tab, comments.length]);

    // Local object URLs for staged image thumbnails; revoked when the list changes.
    const commentFilePreviews = useMemo(
        () => commentFiles.map((f) => (f.type?.startsWith('image/') ? URL.createObjectURL(f) : null)),
        [commentFiles],
    );
    useEffect(() => () => commentFilePreviews.forEach((u) => u && URL.revokeObjectURL(u)), [commentFilePreviews]);

    if (taskDetailsLoading && !task) {
        return (
            <div className={`${cardCls} tw-text-center tw-py-12`}>
                <Icon icon="mdi:loading" className="tw-text-4xl tw-text-blue-500 tw-animate-spin" />
                <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">Loading task...</p>
            </div>
        );
    }
    if (!task || String(task.task_id) !== String(id)) {
        return (
            <div className={`${cardCls} tw-text-center tw-py-12`}>
                <Icon icon="mdi:file-remove-outline" className="tw-text-4xl tw-text-gray-300" />
                <p className="tw-text-gray-500 tw-mt-2 tw-text-sm">Task not found</p>
                <button onClick={() => navigate('/society-portal/tasks/projects')} className="tw-mt-3 tw-text-blue-600 tw-text-sm">Back to projects</button>
            </div>
        );
    }

    const withRefetch = (extra) => ({ taskId: id, ...extra, onSuccess: () => { refetch(); extra?.after?.(); } });

    // Back to the owning project, or the task list when it has none.
    const backTo = task.project?.project_id
        ? `/society-portal/tasks/projects/${task.project.project_id}`
        : '/society-portal/tasks/list';
    const backLabel = task.project?.name || 'Tasks';

    const handleStatusChange = (nextStatus) => {
        if (!nextStatus || nextStatus === task.status) return;
        if (!can(getTransitionPermission(nextStatus))) { notify('warning', 'You do not have permission for this status'); return; }
        dispatch(transitionTask(withRefetch({ status: nextStatus, note: '' })));
    };
    // Validate against the API limits (max 10 files, 500 KB each) before staging.
    const stageCommentFiles = (fileList, alreadyStaged = []) => {
        const incoming = Array.from(fileList || []);
        if (!incoming.length) return alreadyStaged;
        const tooBig = incoming.filter((f) => f.size > MAX_COMMENT_FILE_SIZE);
        if (tooBig.length) notify('error', `${tooBig.map((f) => f.file_name || f.name).join(', ')} exceeds the 500 KB per-file limit`);
        let accepted = incoming.filter((f) => f.size <= MAX_COMMENT_FILE_SIZE);
        const room = MAX_COMMENT_FILES - alreadyStaged.length;
        if (accepted.length > room) {
            notify('warning', `Only ${MAX_COMMENT_FILES} attachments are allowed per comment`);
            accepted = accepted.slice(0, Math.max(0, room));
        }
        return [...alreadyStaged, ...accepted];
    };
    const handlePickCommentFiles = (e) => {
        setCommentFiles((prev) => stageCommentFiles(e.target.files, prev));
        e.target.value = '';
    };
    const removeCommentFile = (index) => setCommentFiles((prev) => prev.filter((_, i) => i !== index));

    // The composer is cleared immediately and the reducer appends the created
    // comment to the thread, so nothing here triggers a refetch (which is what
    // made the panel flash). Comment attachments never appear on the task
    // header either, so task details do not need refreshing.
    const handleAddComment = () => {
        if (!commentBody.trim() || addCommentLoading) return;
        const body = commentBody.trim();
        const files = commentFiles;
        const internal = commentInternal;
        setCommentBody('');
        setCommentFiles([]);
        setCommentInternal(false);
        if (composerRef.current) composerRef.current.style.height = 'auto';
        dispatch(addComment({
            taskId: id,
            body,
            is_internal: internal,
            files, // posted in the same multipart request as the comment
            silent: true,
            onFailure: () => { setCommentBody(body); setCommentFiles(files); setCommentInternal(internal); },
        }));
    };

    // Files added to a comment that was already posted.
    const handleAddFilesToComment = (commentId, fileList) => {
        const files = stageCommentFiles(fileList);
        if (!files.length) return;
        dispatch(uploadCommentAttachments({
            taskId: id,
            commentId,
            files,
            silent: true,
            onSuccess: () => dispatch(getComments({ taskId: id })),
        }));
    };
    const deleteCommentAttachment = (attachmentId) => {
        dispatch(deleteAttachment({
            taskId: id,
            attachmentId,
            silent: true,
            onSuccess: () => dispatch(getComments({ taskId: id })),
        }));
    };
    // Auto-grow the composer up to a few lines, like a chat input.
    const handleCommentInput = (e) => {
        setCommentBody(e.target.value);
        e.target.style.height = 'auto';
        e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
    };
    const handleCommentKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); }
    };
    const uploadFile = (file) => {
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) { notify('error', 'File exceeds 10 MB limit'); return; }
        dispatch(uploadAttachment(withRefetch({ file })));
    };
    const handleUpload = (e) => { uploadFile(e.target.files?.[0]); e.target.value = ''; };
    const downloadFile = async (att) => {
        const aid = att.attachment_id;
        if (downloading[aid] != null) return; // already in progress
        setDownloading((prev) => ({ ...prev, [aid]: 0 }));
        try {
            const total = att.file_size || 0;
            const blob = await downloadAttachmentReq({
                taskId: id,
                attachmentId: aid,
                onDownloadProgress: (e) => {
                    const known = e.total || total;
                    // With a known size show real %, otherwise creep toward 90%.
                    const pct = known
                        ? Math.min(100, Math.round((e.loaded / known) * 100))
                        : Math.min(90, Math.round(e.loaded / 1024));
                    setDownloading((prev) => ({ ...prev, [aid]: pct }));
                },
            });
            const url = window.URL.createObjectURL(new Blob([blob]));
            const a = document.createElement('a');
            a.href = url;
            a.download = att.file_name || 'attachment';
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            notify('error', err?.message || 'Failed to download attachment');
        } finally {
            setDownloading((prev) => {
                const next = { ...prev };
                delete next[aid];
                return next;
            });
        }
    };
    const handleDrop = (e) => { e.preventDefault(); setDragOver(false); if (can(PERMISSIONS.TASK_UPDATE)) uploadFile(e.dataTransfer.files?.[0]); };
    const submitAssignModal = () => {
        const uid = Number(assignUserId);
        if (!Number.isInteger(uid) || uid <= 0) { notify('warning', 'Please select an employee'); return; }
        const done = () => { setAssignModal(null); setAssignUserId(''); setAssignNote(''); };
        if (assignModal === 'assign') dispatch(assignTask(withRefetch({ user_ids: [uid], role: 'ASSIGNEE', after: done })));
        if (assignModal === 'reassign') dispatch(reassignTask(withRefetch({ to_user_id: uid, note: assignNote, after: done })));
        if (assignModal === 'delegate') dispatch(delegateTask(withRefetch({ to_user_id: uid, note: assignNote, after: done })));
    };

    const Row = ({ label, children }) => (
        <div className="tw-flex tw-justify-between tw-gap-3 tw-text-sm tw-px-3 tw-py-2 odd:tw-bg-gray-50/60">
            <span className="tw-text-gray-500">{label}</span>
            <span className="tw-text-gray-800 tw-text-right tw-font-medium">{children}</span>
        </div>
    );

    const attachments = Array.isArray(task.attachments) ? task.attachments : [];
    const activeAssignments = Array.isArray(task.assignments) ? task.assignments.filter((a) => a.status === 'ACTIVE') : [];
    const watchers = Array.isArray(task.watchers) ? task.watchers : [];

    const tabs = [
        { key: 'comments', label: 'Comments', icon: 'mdi:comment-text-outline', count: comments.length },
        { key: 'history', label: 'History', icon: 'mdi:history' },
    ];

    return (
        <>
            {/* Top bar */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
                <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-500 tw-min-w-0">
                    <button onClick={() => navigate(backTo)} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500 tw-flex-shrink-0">
                        <Icon icon="mdi:arrow-left" />
                    </button>
                    <button onClick={() => navigate(backTo)} className="hover:tw-text-gray-700 tw-truncate tw-max-w-[220px]">{backLabel}</button>
                    <Icon icon="mdi:chevron-right" className="tw-text-gray-300 tw-flex-shrink-0" />
                    <span className="tw-text-gray-700 tw-font-medium tw-flex-shrink-0">{task.task_number}</span>
                </div>
                <div className="tw-flex tw-items-center tw-gap-1">
                    <button onClick={() => navigate(-1)} title="Back" className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400">
                        <Icon icon="mdi:chevron-left" />
                    </button>
                    <button onClick={() => navigate(1)} title="Forward" className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400">
                        <Icon icon="mdi:chevron-right" />
                    </button>
                </div>
            </div>

            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-4">
                {/* Left / main column */}
                <div className="lg:tw-col-span-2 tw-space-y-4">
                    {/* Title card */}
                    <div className={`${cardCls} tw-p-5`}>
                        <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
                            <div className="tw-min-w-0">
                                <div className="tw-flex tw-items-baseline tw-gap-2 tw-flex-wrap">
                                    <span className="tw-text-2xl tw-font-bold tw-text-teal-600">{task.task_number}</span>
                                    <h1 className="tw-text-2xl tw-font-semibold tw-text-gray-800 tw-break-words">{task.title}</h1>
                                </div>
                                <p className="tw-text-xs tw-font-semibold tw-text-gray-400 tw-tracking-wider tw-uppercase tw-mt-1">
                                    {(task.source ? humanize(task.source) : 'Task')} · {humanize(task.priority)} priority
                                </p>
                                {/* Tags */}
                                <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-wrap tw-mt-3">
                                    <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-medium ${priorityBadgeClass(task.priority)}`}>{humanize(task.priority)}</span>
                                    {task.department && <span className="tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-medium tw-bg-gray-100 tw-text-gray-600">{humanize(task.department)}</span>}
                                    {task.category && <span className="tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-medium tw-bg-gray-100 tw-text-gray-600">{task.category}</span>}
                                    {task.project?.name && <span className="tw-flex tw-items-center tw-gap-0.5 tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-medium tw-bg-indigo-50 tw-text-indigo-700"><Icon icon="hugeicons:folder-01" /> {task.project.name}</span>}
                                </div>
                            </div>
                            <div className="tw-text-right tw-flex-shrink-0">
                                <p className="tw-text-[11px] tw-text-gray-500">Created by <span className="tw-font-medium tw-text-gray-700">{userName(task.creator)}</span></p>
                                <p className="tw-text-[11px] tw-text-gray-400">{fmt(task.created_at)}</p>
                                <div className="tw-flex tw-justify-end tw-mt-1.5">
                                    <span className="tw-w-9 tw-h-9 tw-rounded-full tw-bg-teal-100 tw-text-teal-700 tw-text-xs tw-font-semibold tw-flex tw-items-center tw-justify-center">{initials(task.creator)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Description */}
                    <div className={`${cardCls} tw-p-5`}>
                        <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                            <p className={sideLabel}>Description</p>
                            {can(PERMISSIONS.TASK_UPDATE) && (
                                <button onClick={() => navigate(`/society-portal/tasks/edit/${id}`)} className="tw-text-gray-400 hover:tw-text-blue-600" title="Edit">
                                    <Icon icon="mdi:pencil-outline" />
                                </button>
                            )}
                        </div>
                        {task.description
                            ? <div className="tw-text-sm tw-text-gray-700" dangerouslySetInnerHTML={{ __html: task.description }} />
                            : <p className="tw-text-sm tw-text-gray-400 tw-italic">Empty space is so boring… go on, be descriptive…</p>}
                        {typeof task.progress_percent === 'number' && (
                            <div className="tw-mt-4">
                                <div className="tw-flex tw-justify-between tw-text-[11px] tw-text-gray-500 tw-mb-1">
                                    <span>Progress</span><span>{task.progress_percent}%</span>
                                </div>
                                <div className="tw-w-full tw-bg-gray-100 tw-rounded-full tw-h-2">
                                    <div className="tw-bg-teal-500 tw-h-2 tw-rounded-full" style={{ width: `${Math.min(100, Math.max(0, task.progress_percent))}%` }} />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Attachments */}
                    <div className={cardCls}>
                        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-bg-gray-50 tw-rounded-t-lg tw-border-b tw-border-gray-100">
                            <p className="tw-text-sm tw-font-semibold tw-text-gray-700">{attachments.length} Attachment{attachments.length === 1 ? '' : 's'}</p>
                            {can(PERMISSIONS.TASK_UPDATE) && (
                                <label className="tw-w-7 tw-h-7 tw-rounded tw-bg-teal-500 hover:tw-bg-teal-600 tw-text-white tw-flex tw-items-center tw-justify-center tw-cursor-pointer" title="Upload">
                                    <Icon icon="mdi:plus" />
                                    <input type="file" className="tw-hidden" onChange={handleUpload} />
                                </label>
                            )}
                        </div>
                        <div className="tw-p-4">
                            {can(PERMISSIONS.TASK_UPDATE) && (
                                <label
                                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                    onDragLeave={() => setDragOver(false)}
                                    onDrop={handleDrop}
                                    className={`tw-block tw-border tw-border-dashed tw-rounded-md tw-text-center tw-py-6 tw-text-sm tw-cursor-pointer tw-transition-colors ${dragOver ? 'tw-border-teal-400 tw-bg-teal-50 tw-text-teal-600' : 'tw-border-gray-200 tw-text-gray-400 hover:tw-bg-gray-50'}`}
                                >
                                    Drop attachments here!
                                    <input type="file" className="tw-hidden" onChange={handleUpload} />
                                </label>
                            )}
                            {attachments.length > 0 && (
                                <ul className="tw-space-y-1.5 tw-mt-3">
                                    {attachments.map((att) => {
                                        const pct = downloading[att.attachment_id];
                                        const isDownloading = pct != null;
                                        return (
                                        <li key={att.attachment_id} className="tw-flex tw-flex-col tw-gap-1.5 tw-bg-gray-50 tw-rounded tw-px-2.5 tw-py-2 tw-text-sm">
                                            <div className="tw-flex tw-items-center tw-justify-between">
                                                <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                                                    <Icon icon="mdi:file-outline" className="tw-text-gray-400 tw-flex-shrink-0" />
                                                    <span className="tw-text-gray-700 tw-truncate">{att.file_name}</span>
                                                    {att.file_size ? <span className="tw-text-[10px] tw-text-gray-400 tw-flex-shrink-0">{Math.round(att.file_size / 1024)} KB</span> : null}
                                                </div>
                                                <div className="tw-flex tw-items-center tw-gap-1 tw-flex-shrink-0">
                                                    <button onClick={() => downloadFile(att)} disabled={isDownloading} title="Download" className="tw-text-gray-400 hover:tw-text-teal-600 disabled:tw-opacity-60 disabled:tw-cursor-default">
                                                        <Icon icon={isDownloading ? 'mdi:loading' : 'mdi:download-outline'} className={isDownloading ? 'tw-animate-spin' : ''} />
                                                    </button>
                                                    {can(PERMISSIONS.TASK_UPDATE) && isMyUpload(att) && (
                                                        <button onClick={() => dispatch(deleteAttachment(withRefetch({ attachmentId: att.attachment_id })))} title="Delete" className="tw-text-gray-400 hover:tw-text-red-500">
                                                            <Icon icon="mdi:delete-outline" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            {isDownloading && (
                                                <div className="tw-flex tw-items-center tw-gap-2">
                                                    <div className="tw-flex-1 tw-h-1 tw-bg-gray-200 tw-rounded tw-overflow-hidden">
                                                        <div className="tw-h-full tw-bg-teal-500 tw-transition-all tw-duration-150" style={{ width: `${pct}%` }} />
                                                    </div>
                                                    <span className="tw-text-[10px] tw-text-gray-500 tw-tabular-nums tw-w-8 tw-text-right">{pct}%</span>
                                                </div>
                                            )}
                                        </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </div>

                    {/* Activity: comments / dependencies / history */}
                    <div className={cardCls}>
                        <div className="tw-flex tw-border-b tw-border-gray-100 tw-overflow-x-auto">
                            {tabs.map((t) => (
                                <button key={t.key} onClick={() => setTab(t.key)}
                                    className={`tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2.5 tw-text-sm tw-font-medium tw-whitespace-nowrap tw-border-b-2 ${tab === t.key ? 'tw-border-teal-600 tw-text-teal-600' : 'tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700'}`}>
                                    <Icon icon={t.icon} /> {t.label}{typeof t.count === 'number' ? ` (${t.count})` : ''}
                                </button>
                            ))}
                        </div>
                        <div className="tw-p-4">
                            {/* Comments */}
                            {tab === 'comments' && (
                                <div>
                                    {/* Chat thread — my messages on the right, everyone else on the left. */}
                                    <div ref={chatBoxRef} className="tw-max-h-[440px] tw-min-h-[200px] tw-overflow-y-auto tw-rounded-xl tw-border tw-border-gray-100 tw-bg-slate-50 tw-px-3 tw-py-3">
                                        {/* Only the first load shows a spinner — later refreshes keep the
                                            thread on screen so it never flashes. */}
                                        {taskCommentsLoading && comments.length === 0 ? (
                                            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12">
                                                <Icon icon="mdi:loading" className="tw-text-2xl tw-text-teal-500 tw-animate-spin" />
                                                <p className="tw-m-0 tw-mt-2 tw-text-sm tw-text-gray-400">Loading comments...</p>
                                            </div>
                                        ) : comments.length === 0 ? (
                                            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12 tw-text-center">
                                                <Icon icon="mdi:message-text-outline" className="tw-text-4xl tw-text-gray-300" />
                                                <p className="tw-m-0 tw-mt-2 tw-text-sm tw-font-medium tw-text-gray-500">No comments yet</p>
                                                <p className="tw-m-0 tw-mt-0.5 tw-text-xs tw-text-gray-400">Start the conversation about this task</p>
                                            </div>
                                        ) : comments.map((c, i) => {
                                            const author = c.author || c.user;
                                            const mine = isMe(author);
                                            const prev = i > 0 ? comments[i - 1] : null;
                                            const prevAuthor = prev ? (prev.author || prev.user) : null;
                                            const newDay = !prev || dayKey(prev.created_at) !== dayKey(c.created_at);
                                            // Consecutive messages from the same person are grouped: no repeated avatar/name.
                                            const grouped = !newDay && prevAuthor && String(prevAuthor.user_id) === String(author?.user_id);
                                            const internal = c.is_internal === 1 || c.is_internal === true;
                                            const edited = c.is_edited === 1 || c.is_edited === true;
                                            const atts = Array.isArray(c.attachments) ? c.attachments : [];
                                            const images = atts.filter(isImage);
                                            const docs = atts.filter((a) => !isImage(a));
                                            return (
                                                <div key={c.comment_id}>
                                                    {newDay && (
                                                        <div className="tw-flex tw-justify-center tw-my-3">
                                                            <span className="tw-px-2.5 tw-py-1 tw-rounded-full tw-bg-white tw-border tw-border-gray-100 tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-gray-500 tw-shadow-sm">
                                                                {dayLabel(c.created_at)}
                                                            </span>
                                                        </div>
                                                    )}
                                                    <div className={`tw-flex tw-items-end tw-gap-2 ${grouped ? 'tw-mt-1' : 'tw-mt-3'} ${mine ? 'tw-flex-row-reverse' : ''}`}>
                                                        {grouped ? (
                                                            <div className="tw-w-7 tw-flex-shrink-0" />
                                                        ) : (
                                                            <div title={userName(author)} className={`tw-w-7 tw-h-7 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-text-[10px] tw-font-bold ${avatarColor(author)}`}>
                                                                {initials(author)}
                                                            </div>
                                                        )}
                                                        <div className={`tw-max-w-[80%] tw-min-w-0 tw-px-3 tw-py-2 tw-shadow-sm tw-border tw-rounded-2xl ${mine
                                                            ? `tw-bg-teal-500 tw-text-white tw-border-teal-500 ${grouped ? 'tw-rounded-tr-md' : ''} tw-rounded-br-sm`
                                                            : `tw-bg-white tw-border-gray-100 ${grouped ? 'tw-rounded-tl-md' : ''} tw-rounded-bl-sm`}`}>
                                                            {!grouped && (
                                                                <div className={`tw-flex tw-items-baseline tw-gap-1.5 tw-flex-wrap tw-mb-0.5 ${mine ? 'tw-justify-end' : ''}`}>
                                                                    <span className={`tw-text-xs tw-font-bold ${mine ? 'tw-text-white' : 'tw-text-gray-800'}`}>
                                                                        {mine ? 'You' : userName(author)}
                                                                    </span>
                                                                    {!mine && author?.email && (
                                                                        <span className="tw-text-[10px] tw-text-gray-400 tw-truncate">{author.email}</span>
                                                                    )}
                                                                </div>
                                                            )}
                                                            <p className={`tw-m-0 tw-text-sm tw-whitespace-pre-wrap tw-break-words ${mine ? 'tw-text-white' : 'tw-text-gray-700'}`}>{c.body}</p>

                                                            {/* Image attachments — inline previews, click opens the full file */}
                                                            {images.length > 0 && (
                                                                <div className={`tw-grid tw-gap-1 tw-mt-1.5 ${images.length === 1 ? 'tw-grid-cols-1' : 'tw-grid-cols-2'}`}>
                                                                    {images.map((att) => (
                                                                        <a key={att.attachment_id} href={fileUrl(att)} target="_blank" rel="noreferrer"
                                                                            title={att.file_name} className="tw-block tw-relative tw-group tw-rounded-lg tw-overflow-hidden tw-bg-black/5">
                                                                            <img src={fileUrl(att)} alt={att.file_name}
                                                                                className={`tw-w-full tw-object-cover ${images.length === 1 ? 'tw-max-h-56' : 'tw-h-24'}`} />
                                                                            {can(PERMISSIONS.TASK_UPDATE) && mine && isMyUpload(att) && (
                                                                                <button type="button"
                                                                                    onClick={(e) => { e.preventDefault(); deleteCommentAttachment(att.attachment_id); }}
                                                                                    title="Delete attachment"
                                                                                    className="tw-absolute tw-top-1 tw-right-1 tw-w-6 tw-h-6 tw-rounded-full tw-bg-black/50 tw-text-white tw-flex tw-items-center tw-justify-center tw-opacity-0 group-hover:tw-opacity-100">
                                                                                    <Icon icon="mdi:delete-outline" className="tw-text-sm" />
                                                                                </button>
                                                                            )}
                                                                        </a>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {/* Non-image attachments — file rows with download */}
                                                            {docs.length > 0 && (
                                                                <ul className="tw-list-none tw-p-0 tw-m-0 tw-mt-1.5 tw-space-y-1">
                                                                    {docs.map((att) => {
                                                                        const pct = downloading[att.attachment_id];
                                                                        const busy = pct != null;
                                                                        return (
                                                                            <li key={att.attachment_id}
                                                                                className={`tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-px-2 tw-py-1.5 ${mine ? 'tw-bg-white/15' : 'tw-bg-gray-50'}`}>
                                                                                <Icon icon={fileIcon(att)} className={`tw-text-base tw-flex-shrink-0 ${mine ? 'tw-text-white' : 'tw-text-gray-400'}`} />
                                                                                <div className="tw-min-w-0 tw-flex-1">
                                                                                    <p className={`tw-m-0 tw-text-xs tw-truncate ${mine ? 'tw-text-white' : 'tw-text-gray-700'}`} title={att.file_name}>{att.file_name}</p>
                                                                                    <p className={`tw-m-0 tw-text-[10px] ${mine ? 'tw-text-white/70' : 'tw-text-gray-400'}`}>
                                                                                        {busy ? `${pct}%` : fileSize(att.file_size)}
                                                                                    </p>
                                                                                </div>
                                                                                <button type="button" onClick={() => downloadFile(att)} disabled={busy} title="Download"
                                                                                    className={`tw-flex-shrink-0 ${mine ? 'tw-text-white/80 hover:tw-text-white' : 'tw-text-gray-400 hover:tw-text-teal-600'} disabled:tw-opacity-60`}>
                                                                                    <Icon icon={busy ? 'mdi:loading' : 'mdi:download-outline'} className={busy ? 'tw-animate-spin' : ''} />
                                                                                </button>
                                                                                {can(PERMISSIONS.TASK_UPDATE) && mine && isMyUpload(att) && (
                                                                                    <button type="button" onClick={() => deleteCommentAttachment(att.attachment_id)} title="Delete attachment"
                                                                                        className={`tw-flex-shrink-0 ${mine ? 'tw-text-white/80 hover:tw-text-white' : 'tw-text-gray-400 hover:tw-text-red-500'}`}>
                                                                                        <Icon icon="mdi:delete-outline" />
                                                                                    </button>
                                                                                )}
                                                                            </li>
                                                                        );
                                                                    })}
                                                                </ul>
                                                            )}

                                                            <div className={`tw-flex tw-items-center tw-gap-1.5 tw-mt-1 ${mine ? 'tw-justify-end' : ''}`}>
                                                                {atts.length > 0 && (
                                                                    <span className={`tw-inline-flex tw-items-center tw-gap-0.5 tw-text-[10px] ${mine ? 'tw-text-white/70' : 'tw-text-gray-400'}`}>
                                                                        <Icon icon="mdi:paperclip" className="tw-text-[11px]" />{atts.length}
                                                                    </span>
                                                                )}
                                                                {mine && can(PERMISSIONS.TASK_COMMENT) && atts.length < MAX_COMMENT_FILES && (
                                                                    <button type="button" title="Add attachments to this comment"
                                                                        onClick={() => { setAttachToCommentId(c.comment_id); commentAttachInputRef.current?.click(); }}
                                                                        className="tw-text-[10px] tw-text-white/70 hover:tw-text-white tw-inline-flex tw-items-center tw-gap-0.5">
                                                                        <Icon icon="mdi:plus" className="tw-text-[11px]" /> files
                                                                    </button>
                                                                )}
                                                                {internal && (
                                                                    <span className={`tw-inline-flex tw-items-center tw-gap-0.5 tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[9px] tw-font-semibold ${mine ? 'tw-bg-white/20 tw-text-white' : 'tw-bg-amber-100 tw-text-amber-700'}`}>
                                                                        <Icon icon="mdi:lock-outline" className="tw-text-[10px]" /> Internal
                                                                    </span>
                                                                )}
                                                                {edited && <span className={`tw-text-[10px] tw-italic ${mine ? 'tw-text-white/70' : 'tw-text-gray-400'}`}>edited</span>}
                                                                <span title={fmt(c.created_at)} className={`tw-text-[10px] ${mine ? 'tw-text-white/70' : 'tw-text-gray-400'}`}>{chatTime(c.created_at)}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Hidden input used by the per-comment "add files" action */}
                                    <input ref={commentAttachInputRef} type="file" multiple className="tw-hidden"
                                        onChange={(e) => {
                                            if (attachToCommentId) handleAddFilesToComment(attachToCommentId, e.target.files);
                                            setAttachToCommentId(null);
                                            e.target.value = '';
                                        }} />

                                    {/* Composer */}
                                    {can(PERMISSIONS.TASK_COMMENT) && (
                                        <div className="tw-mt-3"
                                            onDragOver={(e) => e.preventDefault()}
                                            onDrop={(e) => { e.preventDefault(); setCommentFiles((prev) => stageCommentFiles(e.dataTransfer.files, prev)); }}>
                                            {/* Staged attachments — sent together with the comment */}
                                            {commentFiles.length > 0 && (
                                                <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mb-2 tw-p-2 tw-rounded-xl tw-border tw-border-gray-100 tw-bg-slate-50">
                                                    {commentFiles.map((f, i) => (
                                                        <div key={`${f.name}-${i}`} className="tw-relative tw-flex tw-items-center tw-gap-1.5 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-pl-1.5 tw-pr-6 tw-py-1 tw-max-w-[190px]">
                                                            {commentFilePreviews[i] ? (
                                                                <img src={commentFilePreviews[i]} alt={f.name} className="tw-w-7 tw-h-7 tw-rounded tw-object-cover tw-flex-shrink-0" />
                                                            ) : (
                                                                <Icon icon={fileIcon({ file_type: f.type })} className="tw-text-lg tw-text-gray-400 tw-flex-shrink-0" />
                                                            )}
                                                            <div className="tw-min-w-0">
                                                                <p className="tw-m-0 tw-text-[11px] tw-text-gray-700 tw-truncate" title={f.name}>{f.name}</p>
                                                                <p className="tw-m-0 tw-text-[10px] tw-text-gray-400">{fileSize(f.size)}</p>
                                                            </div>
                                                            <button type="button" onClick={() => removeCommentFile(i)} title="Remove"
                                                                className="tw-absolute tw-top-1 tw-right-1 tw-text-gray-300 hover:tw-text-red-500">
                                                                <Icon icon="mdi:close" className="tw-text-xs" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                    <span className="tw-self-center tw-text-[10px] tw-text-gray-400">{commentFiles.length}/{MAX_COMMENT_FILES}</span>
                                                </div>
                                            )}

                                            <div className={`tw-flex tw-items-end tw-gap-2 tw-rounded-2xl tw-border tw-px-2 tw-py-1.5 focus-within:tw-border-teal-400 ${commentInternal ? 'tw-border-amber-300 tw-bg-amber-50' : 'tw-border-gray-200 tw-bg-white'}`}>
                                                <button type="button" onClick={() => setCommentInternal((v) => !v)}
                                                    title={commentInternal ? 'Internal note — click to make it public' : 'Public comment — click to make it an internal note'}
                                                    className={`tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${commentInternal ? 'tw-bg-amber-100 tw-text-amber-700' : 'tw-text-gray-400 hover:tw-bg-gray-100'}`}>
                                                    <Icon icon={commentInternal ? 'mdi:lock' : 'mdi:lock-open-variant-outline'} className="tw-text-base" />
                                                </button>
                                                <input ref={commentFileInputRef} type="file" multiple className="tw-hidden" onChange={handlePickCommentFiles} />
                                                <button type="button" onClick={() => commentFileInputRef.current?.click()}
                                                    disabled={commentFiles.length >= MAX_COMMENT_FILES}
                                                    title={commentFiles.length >= MAX_COMMENT_FILES ? `Maximum ${MAX_COMMENT_FILES} attachments` : 'Attach files (max 10, 500 KB each)'}
                                                    className="tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-text-gray-400 hover:tw-bg-gray-100 disabled:tw-opacity-40">
                                                    <Icon icon="mdi:paperclip" className="tw-text-base" />
                                                </button>
                                                <textarea ref={composerRef} rows={1} value={commentBody} onChange={handleCommentInput} onKeyDown={handleCommentKeyDown}
                                                    placeholder={commentInternal ? 'Write an internal note...' : 'Write a comment...'}
                                                    className="tw-flex-1 tw-min-w-0 tw-resize-none tw-border-0 tw-bg-transparent tw-py-1.5 tw-text-sm tw-text-gray-700 focus:tw-outline-none" />
                                                <button onClick={handleAddComment} disabled={!commentBody.trim() || addCommentLoading}
                                                    title="Send"
                                                    className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-teal-500 tw-text-white tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 hover:tw-bg-teal-600 disabled:tw-opacity-40 disabled:hover:tw-bg-teal-500">
                                                    <Icon icon={addCommentLoading ? 'mdi:loading' : 'mdi:send'} className={`tw-text-base ${addCommentLoading ? 'tw-animate-spin' : ''}`} />
                                                </button>
                                            </div>
                                            <p className="tw-m-0 tw-mt-1 tw-px-2 tw-text-[11px] tw-text-gray-400">
                                                {uploadCommentAttachmentsLoading
                                                    ? 'Uploading attachments...'
                                                    : commentFiles.length && !commentBody.trim()
                                                        ? 'Add a message to send these attachments'
                                                        : commentInternal
                                                            ? 'Internal note — visible to staff only'
                                                            : 'Press Enter to send · Shift + Enter for a new line · up to 10 files, 500 KB each'}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}


                            {/* History */}
                            {tab === 'history' && (
                                <div>
                                    {taskHistoryLoading ? (
                                        <p className="tw-text-sm tw-text-gray-400 tw-py-4 tw-text-center">Loading history...</p>
                                    ) : history.length === 0 ? (
                                        <p className="tw-text-sm tw-text-gray-400 tw-py-4 tw-text-center">No activity yet</p>
                                    ) : (
                                        <ul className="tw-space-y-3">
                                            {history.map((h, i) => (
                                                <li key={h.history_id || i} className="tw-flex tw-gap-2.5">
                                                    <div className="tw-flex tw-flex-col tw-items-center">
                                                        <div className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-teal-500 tw-mt-1.5" />
                                                        {i < history.length - 1 && <div className="tw-w-px tw-flex-1 tw-bg-gray-200" />}
                                                    </div>
                                                    <div className="tw-pb-1">
                                                        <p className="tw-text-sm tw-text-gray-700">
                                                            <span className="tw-font-medium">{humanize(h.event_type || 'Update')}</span>
                                                            {h.field_name ? <span className="tw-text-gray-500"> · {h.field_name}</span> : null}
                                                            {(h.old_value || h.new_value) ? <span className="tw-text-gray-500"> : {historyValue(h, h.old_value)} → {historyValue(h, h.new_value)}</span> : null}
                                                        </p>
                                                        {h.note && <p className="tw-text-xs tw-text-gray-600 tw-mt-0.5">{h.note}</p>}
                                                        <p className="tw-text-[11px] tw-text-gray-600 tw-mt-0.5 tw-flex tw-items-center tw-gap-1 tw-flex-wrap">
                                                            {(h.actor || h.user) && (
                                                                <span className="tw-inline-flex tw-items-center tw-gap-1 tw-font-semibold tw-text-gray-700">
                                                                    <span className="tw-w-4 tw-h-4 tw-rounded-full tw-bg-gray-200 tw-text-gray-600 tw-text-[7px] tw-font-semibold tw-flex tw-items-center tw-justify-center">{initials(h.actor || h.user)}</span>
                                                                    {isMe(h.actor || h.user) && <span className="tw-text-emerald-600">me:</span>}
                                                                    {userName(h.actor || h.user)}
                                                                </span>
                                                            )}
                                                            <span className="tw-font-medium">· {fmt(h.created_at)}</span>
                                                        </p>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right / sidebar column */}
                <div className="tw-space-y-4">
                    {/* Status */}
                    <div className={`${cardCls} tw-p-4`}>
                        <div className="tw-flex tw-items-center tw-justify-between">
                            <span className="tw-text-lg tw-font-bold tw-text-gray-700">{stateGroup(task.status)}</span>
                            <div className="tw-relative">
                                <select
                                    value={task.status}
                                    disabled={transitionTaskLoading || allowedTransitions.length === 0}
                                    onChange={(e) => handleStatusChange(e.target.value)}
                                    className={`tw-appearance-none tw-pr-7 tw-pl-2.5 tw-py-1 tw-rounded tw-text-xs tw-font-semibold tw-cursor-pointer focus:tw-outline-none ${statusBadgeClass(task.status)}`}
                                >
                                    <option value={task.status}>{humanize(task.status)}</option>
                                    {allowedTransitions.map((s) => (
                                        <option key={s} value={s} disabled={!can(getTransitionPermission(s))}>
                                            {humanize(s)}{!can(getTransitionPermission(s)) ? ' (no access)' : ''}
                                        </option>
                                    ))}
                                </select>
                                <Icon icon={transitionTaskLoading ? 'mdi:loading' : 'mdi:chevron-down'} className={`tw-absolute tw-right-1.5 tw-top-1/2 tw--translate-y-1/2 tw-pointer-events-none tw-text-current ${transitionTaskLoading ? 'tw-animate-spin' : ''}`} />
                            </div>
                        </div>
                        {allowedTransitions.length === 0 && (
                            <p className="tw-text-[11px] tw-text-gray-400 tw-mt-2">No transitions available.</p>
                        )}
                    </div>

                    {/* Details */}
                    <div className={cardCls}>
                        <p className={`${sideLabel} tw-ml-3 tw-mt-3 tw-mb-1`}>Details</p>
                        <Row label="Department">{task.department ? humanize(task.department) : '—'}</Row>
                        <Row label="Category">{task.category || '—'}</Row>
                        <Row label="Source">{task.source ? humanize(task.source) : '—'}</Row>
                        <Row label="Start">{fmtDate(task.start_date)}</Row>
                        <Row label="Due">{fmtDate(task.due_date)}</Row>
                        <Row label="SLA Due">{fmt(task.sla_due_at)}</Row>
                        <Row label="Completed">{fmt(task.completed_at)}</Row>
                        <Row label="Estimated">{task.estimated_minutes ? `${task.estimated_minutes} min` : '—'}</Row>
                        <Row label="Actual">{task.actual_minutes ? `${task.actual_minutes} min` : '—'}</Row>
                    </div>

                    {/* Assigned */}
                    <div className={`${cardCls} tw-p-4`}>
                        <p className={`${sideLabel} tw-mb-2`}>Assigned</p>
                        <div className="tw-grid tw-grid-cols-2 tw-gap-2 tw-mb-3">
                            {can(PERMISSIONS.TASK_ASSIGN) && (
                                <button onClick={() => setAssignModal('assign')} className="tw-flex tw-items-center tw-justify-center tw-gap-1 tw-px-2 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-xs tw-text-gray-700 hover:tw-bg-gray-50">
                                    <Icon icon="mdi:plus" /> Add assigned
                                </button>
                            )}
                            <button onClick={() => dispatch(selfAssignTask(withRefetch({})))} className="tw-flex tw-items-center tw-justify-center tw-gap-1 tw-px-2 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-xs tw-text-gray-700 hover:tw-bg-gray-50">
                                <Icon icon="mdi:account-check-outline" /> Assign to me
                            </button>
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-py-1">
                            <span className="tw-w-7 tw-h-7 tw-rounded-full tw-bg-amber-100 tw-text-amber-700 tw-text-[10px] tw-font-semibold tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">{task.assignee ? initials(task.assignee) : '—'}</span>
                            <span className="tw-text-gray-700 tw-flex-1 tw-truncate">{userName(task.assignee)}</span>
                            <span className="tw-text-[10px] tw-text-gray-400">Primary</span>
                        </div>
                        {activeAssignments.map((a) => (
                            <div key={a.user_id + '-' + a.role} className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-py-1">
                                <span className="tw-w-7 tw-h-7 tw-rounded-full tw-bg-gray-100 tw-text-gray-600 tw-text-[10px] tw-font-semibold tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">{initials(a.user)}</span>
                                <span className="tw-text-gray-700 tw-flex-1 tw-truncate">{userName(a.user)}</span>
                                <span className="tw-text-[10px] tw-text-gray-400">{humanize(a.role)}</span>
                                {can(PERMISSIONS.TASK_ASSIGN) && (
                                    <button onClick={() => dispatch(removeAssignee(withRefetch({ userId: a.user_id })))} className="tw-text-gray-400 hover:tw-text-red-500">
                                        <Icon icon="mdi:close" />
                                    </button>
                                )}
                            </div>
                        ))}
                        <div className="tw-flex tw-items-center tw-gap-3 tw-mt-2 tw-pt-2 tw-border-t tw-border-gray-50">
                            {can(PERMISSIONS.TASK_REASSIGN) && <button onClick={() => setAssignModal('reassign')} className="tw-text-xs tw-text-blue-600 hover:tw-underline">Reassign</button>}
                            {can(PERMISSIONS.TASK_DELEGATE) && <button onClick={() => setAssignModal('delegate')} className="tw-text-xs tw-text-blue-600 hover:tw-underline">Delegate</button>}
                        </div>
                    </div>

                    {/* Watchers */}
                    <div className={`${cardCls} tw-p-4`}>
                        <p className={`${sideLabel} tw-mb-2`}>Watchers</p>
                        <div className="tw-space-y-2 tw-mb-3">
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <div className="tw-flex-1 tw-min-w-0">
                                    <SearchableDropdown value={watcherId} onChange={(v) => setWatcherId(v === 'all' ? '' : v)}
                                        options={employeeOptions} loading={employeesDropdownLoading} placeholder="Select employee" small />
                                </div>
                                <button onClick={() => { const uid = Number(watcherId); if (uid > 0) dispatch(addWatcher(withRefetch({ user_id: uid, after: () => setWatcherId('') }))); }}
                                    className="tw-px-3 tw-py-1.5 tw-bg-teal-500 hover:tw-bg-teal-600 tw-text-white tw-rounded-md tw-text-xs tw-font-medium tw-whitespace-nowrap disabled:tw-opacity-50"
                                    disabled={!watcherId}>
                                    Add
                                </button>
                            </div>
                            <button onClick={() => dispatch(addWatcher(withRefetch({})))} className="tw-w-full tw-flex tw-items-center tw-justify-center tw-gap-1 tw-px-2 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-xs tw-text-gray-700 hover:tw-bg-gray-50">
                                <Icon icon="mdi:eye-outline" /> Watch this task
                            </button>
                        </div>
                        {watchers.length === 0 ? (
                            <p className="tw-text-sm tw-text-gray-400">No watchers</p>
                        ) : watchers.map((w) => (
                            <div key={w.user_id} className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-py-1">
                                <span className="tw-w-7 tw-h-7 tw-rounded-full tw-bg-gray-100 tw-text-gray-600 tw-text-[10px] tw-font-semibold tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">{initials(w.user)}</span>
                                <span className="tw-text-gray-700 tw-flex-1 tw-truncate">{userName(w.user)}</span>
                                <button onClick={() => dispatch(removeWatcher(withRefetch({ userId: w.user_id })))} className="tw-text-gray-400 hover:tw-text-red-500">
                                    <Icon icon="mdi:close" />
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* Action icons */}
                    <div className={`${cardCls} tw-p-3 tw-flex tw-items-center tw-justify-around`}>
                        <button onClick={() => setTab('history')} title="History" className="tw-w-9 tw-h-9 tw-rounded tw-bg-gray-50 hover:tw-bg-gray-100 tw-text-gray-500 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="mdi:history" />
                        </button>
                        {can(PERMISSIONS.TASK_ASSIGN) && (
                            <button onClick={() => setAssignModal('assign')} title="Assign" className="tw-w-9 tw-h-9 tw-rounded tw-bg-gray-50 hover:tw-bg-gray-100 tw-text-gray-500 tw-flex tw-items-center tw-justify-center">
                                <Icon icon="mdi:account-multiple-outline" />
                            </button>
                        )}
                        {can(PERMISSIONS.TASK_CREATE) && (
                            <button onClick={() => { setCloneTitle(''); setCloneOpen(true); }} title="Clone" className="tw-w-9 tw-h-9 tw-rounded tw-bg-gray-50 hover:tw-bg-gray-100 tw-text-gray-500 tw-flex tw-items-center tw-justify-center">
                                <Icon icon="mdi:content-copy" />
                            </button>
                        )}
                        {can(PERMISSIONS.TASK_UPDATE) && (
                            <button onClick={() => navigate(`/society-portal/tasks/edit/${id}`)} title="Edit" className="tw-w-9 tw-h-9 tw-rounded tw-bg-gray-50 hover:tw-bg-gray-100 tw-text-gray-500 tw-flex tw-items-center tw-justify-center">
                                <Icon icon="mdi:pencil-outline" />
                            </button>
                        )}
                        {can(PERMISSIONS.TASK_DELETE) && (
                            <button onClick={() => setDeleteOpen(true)} title="Delete" className="tw-w-9 tw-h-9 tw-rounded tw-bg-gray-50 hover:tw-bg-red-50 tw-text-gray-500 hover:tw-text-red-500 tw-flex tw-items-center tw-justify-center">
                                <Icon icon="mdi:trash-can-outline" />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Assign / Reassign / Delegate modal */}
            {assignModal && (
                <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-p-5 tw-max-w-md tw-w-full tw-mx-4">
                        <h3 className="tw-text-base tw-font-semibold tw-text-gray-800 tw-mb-3 tw-capitalize">{assignModal} Task</h3>
                        <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Employee</label>
                        <div className="tw-mb-3">
                            <SearchableDropdown value={assignUserId} onChange={(v) => setAssignUserId(v === 'all' ? '' : v)}
                                options={employeeOptions} loading={employeesDropdownLoading} placeholder="Select employee" />
                        </div>
                        {assignModal !== 'assign' && (
                            <>
                                <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">Note (optional)</label>
                                <input type="text" value={assignNote} onChange={(e) => setAssignNote(e.target.value)} className={`${inputCls} tw-mb-3`} />
                            </>
                        )}
                        <div className="tw-flex tw-justify-end tw-gap-2">
                            <button onClick={() => { setAssignModal(null); setAssignUserId(''); setAssignNote(''); }} className="tw-px-4 tw-py-2 tw-text-sm tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg">Cancel</button>
                            <button onClick={submitAssignModal} className="tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-rounded-lg tw-capitalize">{assignModal}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Clone modal */}
            {cloneOpen && (
                <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-p-5 tw-max-w-md tw-w-full tw-mx-4">
                        <h3 className="tw-text-base tw-font-semibold tw-text-gray-800 tw-mb-3">Clone Task</h3>
                        <label className="tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1">New title (optional)</label>
                        <input type="text" value={cloneTitle} onChange={(e) => setCloneTitle(e.target.value)} placeholder={task.title} className={`${inputCls} tw-mb-3`} />
                        <div className="tw-flex tw-justify-end tw-gap-2">
                            <button onClick={() => setCloneOpen(false)} disabled={cloneTaskLoading} className="tw-px-4 tw-py-2 tw-text-sm tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg disabled:tw-opacity-50">Cancel</button>
                            <button onClick={() => dispatch(cloneTask({ taskId: id, title: cloneTitle || undefined, copy_assignees: true, onSuccess: (res) => { setCloneOpen(false); const nid = res?.data?.task_id; if (nid) navigate(`/society-portal/tasks/view/${nid}`); } }))}
                                disabled={cloneTaskLoading} className="tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-rounded-lg disabled:tw-opacity-50 tw-flex tw-items-center tw-gap-1.5">
                                {cloneTaskLoading ? <><Icon icon="mdi:loading" className="tw-animate-spin" /> Cloning...</> : 'Clone'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete modal */}
            {deleteOpen && (
                <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
                    <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-p-6 tw-max-w-md tw-w-full tw-mx-4">
                        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
                            <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-red-100 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                <Icon icon="mdi:delete-alert" className="tw-text-red-600 tw-text-xl" />
                            </div>
                            <div>
                                <h3 className="tw-text-lg tw-font-semibold tw-text-gray-800">Delete Task</h3>
                                <p className="tw-text-sm tw-text-gray-500">This action cannot be undone</p>
                            </div>
                        </div>
                        <p className="tw-text-sm tw-text-gray-600 tw-mb-6">Delete <span className="tw-font-semibold">{task.title}</span>?</p>
                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
                            <button onClick={() => setDeleteOpen(false)} disabled={deleteTaskLoading} className="tw-px-4 tw-py-2 tw-text-sm tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg disabled:tw-opacity-50">Cancel</button>
                            <button onClick={() => dispatch(deleteTask({ taskId: id, onSuccess: () => navigate(backTo) }))} disabled={deleteTaskLoading}
                                className="tw-px-4 tw-py-2 tw-text-sm tw-text-white tw-bg-red-600 hover:tw-bg-red-700 tw-rounded-lg tw-flex tw-items-center tw-gap-2 disabled:tw-opacity-50">
                                {deleteTaskLoading ? <><Icon icon="mdi:loading" className="tw-animate-spin" /> Deleting...</> : <><Icon icon="mdi:delete" /> Delete</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ViewTask;
